import {
  forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes,
  type ReactNode, type SelectHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { FolderArt } from './FolderArt';
import type { ApiError } from '../lib/api';

/* ---- Button -------------------------------------------------------------- */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  block?: boolean;
  size?: 'md' | 'sm';
  loading?: boolean;
}
export function Button({
  variant = 'primary', block, size = 'md', loading, children, className = '', disabled, ...rest
}: ButtonProps) {
  const cls = [
    'btn', `btn-${variant}`, block && 'btn-block', size === 'sm' && 'btn-sm', className,
  ].filter(Boolean).join(' ');
  return (
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading && <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} aria-hidden />}
      {children}
    </button>
  );
}

/** Link styled as a button, same look, correct semantics for navigation. */
export function ButtonLink({ to, variant = 'primary', block, children }: {
  to: string; variant?: Variant; block?: boolean; children: ReactNode;
}) {
  const cls = ['btn', `btn-${variant}`, block && 'btn-block'].filter(Boolean).join(' ');
  return <Link className={cls} to={to}>{children}</Link>;
}

/* ---- Card ---------------------------------------------------------------- */
export function Card({ title, children, className = '', bodyClassName = '' }: {
  title?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {title && <h2 className="card-title">{title}</h2>}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/* ---- Field + inputs ------------------------------------------------------ */
export function Field({ label, htmlFor, hint, error, children }: {
  label: string; htmlFor?: string; hint?: string; error?: string; children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error" role="alert">{error}</span>}
    </div>
  );
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> { invalid?: boolean; }
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ invalid, className = '', ...rest }, ref) => (
    <input ref={ref} className={`input ${invalid ? 'input-invalid' : ''} ${className}`} {...rest} />
  ),
);
TextInput.displayName = 'TextInput';

/** Password field with a reveal toggle (Login/Registration specs 7.1/7.2). */
export function PasswordInput({ invalid, ...rest }: TextInputProps) {
  const [shown, setShown] = useState(false);
  return (
    <div className="input-group">
      <input
        type={shown ? 'text' : 'password'}
        className={`input ${invalid ? 'input-invalid' : ''}`}
        autoComplete={rest.autoComplete ?? 'current-password'}
        {...rest}
      />
      <button type="button" className="input-reveal" onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide password' : 'Show password'}>
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

export function Select({ invalid, className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return <select className={`input ${invalid ? 'input-invalid' : ''} ${className}`} {...rest}>{children}</select>;
}

/* ---- Status pill --------------------------------------------------------- */
type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

// Domain status → tone, from Stage 1B 3.2 colour pairs. A pill always carries
// its label; colour is never the only signal of a consequential state.
const STATUS_TONE: Record<string, Tone> = {
  paid: 'success', occupied: 'success', active: 'success', completed: 'success', clean: 'success',
  outstanding: 'warning', partial: 'warning', pending: 'warning', reserved: 'warning',
  expiring: 'warning', under_maintenance: 'warning', trialing: 'warning',
  overdue: 'danger', failed: 'danger', suspended: 'danger', cancelled: 'danger', terminated: 'danger',
  vacant: 'info', draft: 'neutral', inactive: 'neutral', former: 'neutral', reversed: 'neutral',
};
export function StatusPill({ status, tone }: { status: string; tone?: Tone }) {
  const t = tone ?? STATUS_TONE[status.toLowerCase()] ?? 'neutral';
  const label = status.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className={`pill pill-${t}`}>{label}</span>;
}

/* ---- Alert --------------------------------------------------------------- */
export function Alert({ tone = 'info', children }: { tone?: 'info' | 'success' | 'warning' | 'danger'; children: ReactNode }) {
  return <div className={`alert alert-${tone}`} role={tone === 'danger' ? 'alert' : undefined}>{children}</div>;
}

/* ---- KPI card ------------------------------------------------------------ */
export function Kpi({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="card kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  );
}

/* ---- states -------------------------------------------------------------- */
export function Spinner() { return <span className="spinner" role="status" aria-label="Loading" />; }

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return <div className="state"><Spinner /><span>{label}</span></div>;
}

/** Every band that fails shows this: a plain sentence and a retry (4.7). */
export function ErrorBlock({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <div className="state">
      <span className="state-title">This didn’t load</span>
      <span>{error.message}</span>
      <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>
    </div>
  );
}

export function EmptyState({ title, message, action, art }: { title: string; message: string; action?: ReactNode; art?: string }) {
  return (
    <div className="state">
      {art && <FolderArt name={art} />}
      <span className="state-title">{title}</span>
      <span>{message}</span>
      {action}
    </div>
  );
}
